import {
  fetchPageAssets,
  pageAssetExists,
  pageLocaleVariantAvailability,
} from '../../ducks/hostedAssets.duck';
import {
  detectBrowserLocale,
  getLocaleFromCurrentUser,
  getLocaleFromLocalStorage,
  getLocaleFromSearchParams,
  getLocaleFromPathname,
  normalizeLocale,
} from '../../util/genericHelpers';
import { THAI_PAGE_IDS } from '../../util/enums';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getNewestPublishedListings } from '../../util/newListings';

export const ASSET_NAME = 'landing-page';
export const NEWEST_LISTINGS_PAGE_SIZE = 8;

export const FETCH_NEWEST_LISTINGS_REQUEST = 'app/LandingPage/FETCH_NEWEST_LISTINGS_REQUEST';
export const FETCH_NEWEST_LISTINGS_SUCCESS = 'app/LandingPage/FETCH_NEWEST_LISTINGS_SUCCESS';
export const FETCH_NEWEST_LISTINGS_ERROR = 'app/LandingPage/FETCH_NEWEST_LISTINGS_ERROR';

const initialState = {
  newestListingIds: [],
  newestListingsInProgress: false,
  newestListingsError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_NEWEST_LISTINGS_REQUEST:
      return {
        ...state,
        newestListingIds: [],
        newestListingsInProgress: true,
        newestListingsError: null,
      };
    case FETCH_NEWEST_LISTINGS_SUCCESS:
      return {
        ...state,
        newestListingIds: payload.listingIds,
        newestListingsInProgress: false,
      };
    case FETCH_NEWEST_LISTINGS_ERROR:
      return {
        ...state,
        newestListingsInProgress: false,
        newestListingsError: payload,
      };
    default:
      return state;
  }
}

export const fetchNewestListingsRequest = () => ({ type: FETCH_NEWEST_LISTINGS_REQUEST });

export const fetchNewestListingsSuccess = listings => ({
  type: FETCH_NEWEST_LISTINGS_SUCCESS,
  payload: { listingIds: listings.map(listing => listing.id) },
});

export const fetchNewestListingsError = error => ({
  type: FETCH_NEWEST_LISTINGS_ERROR,
  error: true,
  payload: error,
});

export const fetchNewestListings = config => (dispatch, _getState, sdk) => {
  dispatch(fetchNewestListingsRequest());

  const referenceDate = new Date();

  const validListingTypesMaybe = config?.listing?.enforceValidListingType
    ? {
        pub_listingType: (config?.listing?.listingTypes || []).map(
          listingType => listingType.listingType
        ),
      }
    : {};
  const queryParams = {
    page: 1,
    perPage: NEWEST_LISTINGS_PAGE_SIZE,
    sort: 'createdAt',
    ...validListingTypesMaybe,
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'createdAt',
      'deleted',
      'state',
      'metadata',
      'publicData',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [
      'variants.scaled-xlarge',
      'variants.scaled-xlarge-2x',
      'variants.scaled-xlarge-6x',
    ],
    'limit.images': 4,
  };

  return sdk.listings
    .query(queryParams)
    .then(response => {
      const publicListings = response?.data?.data || [];
      const newestListings = getNewestPublishedListings(publicListings, referenceDate).slice(
        0,
        NEWEST_LISTINGS_PAGE_SIZE
      );
      const sanitizeConfig = { listingFields: config?.listing?.listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(fetchNewestListingsSuccess(newestListings));
      return newestListings;
    })
    .catch(error => {
      dispatch(fetchNewestListingsError(error));
      throw error;
    });
};

export const loadData = (params, search, config) => async (dispatch, getState) => {
  const { currentUser } = getState().user || {};
  const isLoggedIn = !!currentUser?.id;

  const localeFromSearch = getLocaleFromSearchParams(search);
  const localeFromPath =
    params.locale ||
    (typeof window !== 'undefined' ? getLocaleFromPathname(window.location?.pathname) : null);
  const localeFromStorage = getLocaleFromLocalStorage();
  const localeFromUser = getLocaleFromCurrentUser(currentUser);
  const localeFromConfig = config?.localization?.locale;
  const detectedLocale = detectBrowserLocale();

  // ✅ Final locale resolution (same rules as IntlFromUser)
  const locale = normalizeLocale(
    localeFromSearch ||
      localeFromPath ||
      (isLoggedIn ? localeFromUser : null) ||
      localeFromStorage ||
      localeFromConfig ||
      detectedLocale ||
      'en'
  );

  const originalId = ASSET_NAME;
  const thaiId = THAI_PAGE_IDS?.LANDINGPAGE_ID || null;
  const shouldLoadThai = locale?.toLowerCase().startsWith('th') && thaiId;

  const fallbackAsset = { landingPage: `content/pages/${originalId}.json` };
  const thaiPath = thaiId ? `content/pages/${thaiId}.json` : null;

  if (shouldLoadThai) {
    const thaiAsset = { landingPage: thaiPath };
    const localizedAssets = await dispatch(fetchPageAssets(thaiAsset, true));
    const hasThaiVariant = Boolean(localizedAssets?.landingPage);
    dispatch(pageLocaleVariantAvailability(originalId, hasThaiVariant));
    if (hasThaiVariant) {
      return localizedAssets;
    }

    return dispatch(fetchPageAssets(fallbackAsset, true));
  }

  const [englishAssets, hasThaiVariant] = await Promise.all([
    dispatch(fetchPageAssets(fallbackAsset, true)),
    thaiPath ? dispatch(pageAssetExists(thaiPath)) : Promise.resolve(false),
  ]);
  dispatch(pageLocaleVariantAvailability(originalId, hasThaiVariant));
  return englishAssets;
};
